import React, { useState } from "react";
import "./SentimentAnalysis.css";
import config from "../../config.json";

const SentimentAnalysis = () => {
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [sentimentData, setSentimentData] = useState(null);
    const [graphOptions, setGraphOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [entryCount, setEntryCount] = useState(50);
    const [activeTab, setActiveTab] = useState("Dashboard");
    const [summaryType, setSummaryType] = useState("Positive");
    const [fadeClass, setFadeClass] = useState("fade-in");
    const [selectedGraph, setSelectedGraph] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError("");
        }
    };

    const uploadAndAnalyze = async () => {
        if (!file) {
            setError("Please upload a file first!");
            return;
        }

        setLoading(true);
        setError("");
        setSentimentData(null);

        const formData = new FormData();
        formData.append("file", file);

        try {
            const uploadResponse = await fetch(`${config.SENTIMENTBACKENDURL}/upload`, {
                method: "POST",
                body: formData,
            });

            if (!uploadResponse.ok) throw new Error("Failed to upload file.");

            const uploadData = await uploadResponse.json();
            const filePath = uploadData.file_path;

            const analyzeResponse = await fetch(`${config.SENTIMENTBACKENDURL}/analyse`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ file_path: filePath, entry_count: entryCount }),
            });

            if (!analyzeResponse.ok) throw new Error("Failed to analyze sentiment.");

            const analysisData = await analyzeResponse.json();

            setGraphOptions(analysisData.graph_options || []);
            setSentimentData(analysisData);
        } catch (err) {
            setError(err.message);
        }

        setLoading(false);
    };

    const downloadReport = () => {
        if (!sentimentData) return;

        fetch(`${config.SENTIMENTBACKENDURL}/download_report`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                format: "docx",
                Overall_sentiment: sentimentData.Overall_sentiment,
                Score: sentimentData.Score,
                Positive_summary: sentimentData.Positive_summary,
                Negative_summary: sentimentData.Negative_summary,
                selected_graph_filename: selectedGraph ? selectedGraph.filename : null,
                wordcloud_pos_filename: sentimentData.wordcloud_pos_filename,
                wordcloud_neg_filename: sentimentData.wordcloud_neg_filename,
                compound_csv_filename: sentimentData.compound_csv_filename
            }),
        })
            .then((res) => res.blob())
            .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "Sentiment_Report.docx";
                link.click();
            })
            .catch((err) => console.error("Report Download Error:", err));
    };

    const switchSummary = (type) => {
        setFadeClass("fade-out");
        setTimeout(() => {
            setSummaryType(type);
            setFadeClass("fade-in");
        }, 300);
    };

    const handleReset = () => {
        setFile(null);
        setFileName("");
        setSentimentData(null);
        setGraphOptions([]);
        setLoading(false);
        setError("");
        setEntryCount(50);
        setActiveTab("Dashboard");
        setSummaryType("Positive");
        setSelectedGraph(null);
    };

    const changeTab = (tab) => {
        setFadeClass("fade-out");
        setTimeout(() => {
            setActiveTab(tab);
            setFadeClass("fade-in");
        }, 300);
    };

    return (
        <div className={sentimentData ? "sentiment-container results-active" : "upload-container"}>
            <h2>EmotionFlow AI</h2>

            {sentimentData && sentimentData.Dynamic_purpose && (
                <p
                    style={{
                        fontSize: "0.9rem",
                        color: "black",
                        marginTop: "-10px",
                    }}
                >
                    {sentimentData.Dynamic_purpose}
                </p>
            )}


            {!sentimentData && (
                <>
                    <div className="file-upload">
                        <input type="file" accept=".csv,.xlsx" onChange={handleFileChange} />
                    </div>

                    <div className="range-row">
                    <p className="review-count">Review Count:</p>
                    <div className="range-selector">
                        <input
                        type="number"
                        id="entry-count"
                        min="1"
                        max="100000"
                        value={entryCount}
                        onChange={(e) => setEntryCount(Number(e.target.value))}
                        />
                    </div>
                    </div>


                    <button onClick={uploadAndAnalyze} disabled={loading}>
                        {loading ? "Processing..." : "Upload & Analyze"}
                    </button>

                    {error && <p className="error-message">{error}</p>}
                </>
            )}

            {sentimentData && (
                <div className="results">
                    <div className="tab-buttons">
                        {["Dashboard", "Graphs", "Summary", "Report", "Reset"].map((tab) => (
                            <button
                                key={tab}
                                className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                                onClick={() => changeTab(tab)}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {activeTab === "Dashboard" && (
                    <div className={`dashboard-tab ${fadeClass}`}>
                       {/* KPI Cards */}
                        <div className="kpi-cards">
                        <div className="kpi-card">
                            <h4>Total Reviews</h4>
                            <p>{sentimentData.total_entries}</p>
                        </div>

                        <div className="kpi-card strongly-positive">
                            <h4>Strongly Positive</h4>
                            <p>
                            {sentimentData.total_entries
                                ? (
                                    ((sentimentData.counts["Strongly Positive"] || 0) /
                                    sentimentData.total_entries) *
                                    100
                                ).toFixed(1)
                                : 0}
                            %
                            </p>
                        </div>

                        <div className="kpi-card positive">
                            <h4>Positive</h4>
                            <p>
                            {sentimentData.total_entries
                                ? (
                                    ((sentimentData.counts["Positive"] || 0) /
                                    sentimentData.total_entries) *
                                    100
                                ).toFixed(1)
                                : 0}
                            %
                            </p>
                        </div>

                        <div className="kpi-card neutral">
                            <h4>Neutral</h4>
                            <p>
                            {sentimentData.total_entries
                                ? (
                                    ((sentimentData.counts["Neutral"] || 0) /
                                    sentimentData.total_entries) *
                                    100
                                ).toFixed(1)
                                : 0}
                            %
                            </p>
                        </div>

                        <div className="kpi-card negative">
                            <h4>Negative</h4>
                            <p>
                            {sentimentData.total_entries
                                ? (
                                    ((sentimentData.counts["Negative"] || 0) /
                                    sentimentData.total_entries) *
                                    100
                                ).toFixed(1)
                                : 0}
                            %
                            </p>
                        </div>

                        <div className="kpi-card strongly-negative">
                            <h4>Strongly Negative</h4>
                            <p>
                            {sentimentData.total_entries
                                ? (
                                    ((sentimentData.counts["Strongly Negative"] || 0) /
                                    sentimentData.total_entries) *
                                    100
                                ).toFixed(1)
                                : 0}
                            %
                            </p>
                        </div>

                       
                    </div>


                    {/* Sentiment Insight */}
                    <div
                    className={`overall-sentiment insight-inline ${sentimentData.Overall_sentiment.replace(
                        " ",
                        "-"
                    ).toLowerCase()}`}
                    >
                    <h3>
                        <strong style={{ color: "black" }}>Sentiment Insight:</strong>{" "}
                        <span
                        className={`explanation-text ${sentimentData.Overall_sentiment.replace(
                            " ",
                            "-"
                        ).toLowerCase()}`}
                        >
                        {sentimentData.Overall_explanation}{" "}
                        <span className="sentiment-label">
                            ({sentimentData.Overall_sentiment})
                        </span>
                        </span>
                    </h3>

                    <p className="sentiment-score">
                        Score: {sentimentData.Score ? sentimentData.Score.toFixed(2) : "0.00"}
                    </p>
                    </div>

                        {/* Breakdown */}
                        <div className="additional-info">
                        <li>Total Reviews Analyzed: {sentimentData.total_entries}</li>
                        <li style={{ color: "darkgreen" }}>
                            Strongly Positive: {sentimentData.counts["Strongly Positive"] || 0}
                        </li>
                        <li style={{ color: "green" }}>
                            Positive: {sentimentData.counts["Positive"] || 0}
                        </li>
                        <li>Neutral: {sentimentData.counts["Neutral"] || 0}</li>
                        <li style={{ color: "red" }}>
                            Negative: {sentimentData.counts["Negative"] || 0}
                        </li>
                        <li style={{ color: "darkred" }}>
                            Strongly Negative: {sentimentData.counts["Strongly Negative"] || 0}
                        </li>
                        </div>

                        {/* Highlighted Insights */}
                        <div className="insights-box">
                        <h4>Highlighted Insights</h4>
                        <p>
                            Most frequent sentiment:{" "}
                            <strong>{sentimentData.Overall_sentiment}</strong>

                        </p>
                        <p>
                            Strongest sentiment score:{" "}
                            <strong>
                            {sentimentData.Score > 0
                                ? "Positive"
                                : sentimentData.Score < 0
                                ? "Negative"
                                : "Neutral"}
                            </strong>
                        </p>
                        </div>

                        {/* Score Range Guide */}
                        <div className="score-range-guide">
                        <h4>Score Range Guide</h4>
                        <ul>
                            <li className={sentimentData.Overall_sentiment === "Strongly Positive" ? "highlight" : ""}>
                            <strong>Strongly Positive:</strong> 0.6 to 1.0
                            </li>
                            <li className={sentimentData.Overall_sentiment === "Positive" ? "highlight" : ""}>
                            <strong>Positive:</strong> 0.05 to 0.6
                            </li>
                            <li className={sentimentData.Overall_sentiment === "Neutral" ? "highlight" : ""}>
                            <strong>Neutral:</strong> -0.05 to 0.05
                            </li>
                            <li className={sentimentData.Overall_sentiment === "Negative" ? "highlight" : ""}>
                            <strong>Negative:</strong> -0.05 to -0.6
                            </li>
                            <li className={sentimentData.Overall_sentiment === "Strongly Negative" ? "highlight" : ""}>
                            <strong>Strongly Negative:</strong> -0.6 to -1.0
                            </li>
                        </ul>
                        </div>

                    </div>
                    )}

                    {activeTab === "Graphs" && (
                        <div className={`graphs-tab ${fadeClass}`}>
                            <h4 style={{ marginTop: "20px" }}>Select a Graph</h4>
                            <select
                                value={selectedGraph ? selectedGraph.key : ""}
                                onChange={(e) => {
                                    const selected = graphOptions.find(g => g.key === e.target.value);
                                    setSelectedGraph(selected || null);
                                }}
                            >
                                <option value="">-- Choose a Graph --</option>
                                {graphOptions.map((graph) => (
                                    <option key={graph.key} value={graph.key}>
                                        {graph.title}
                                    </option>
                                ))}
                            </select>

                            {selectedGraph && (
                                <div className="graph-container">
                                    <h4>{selectedGraph.title}</h4>
                                    <img
                                        src={selectedGraph.url}
                                        alt={selectedGraph.title}
                                        className="sentiment-plot"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "Summary" && (
                        <div className={`summary-tab ${fadeClass}`}>
                            <div className="summary-toggle-buttons">
                                <button
                                    className={summaryType === "Positive" ? "active" : ""}
                                    onClick={() => switchSummary("Positive")}
                                >
                                    Positive Summary
                                </button>
                                <button
                                    className={summaryType === "Negative" ? "active" : ""}
                                    onClick={() => switchSummary("Negative")}
                                >
                                    Negative Summary
                                </button>
                            </div>

                            <div className="summary-section">
                                <h4>{summaryType} Summary</h4>
                                <p>{summaryType === "Positive" ? sentimentData.Positive_summary : sentimentData.Negative_summary}</p>
                                <img
                                    src={`${config.SENTIMENTBACKENDURL}/plots/${summaryType === "Positive"
                                        ? sentimentData.wordcloud_pos_filename
                                        : sentimentData.wordcloud_neg_filename}`}
                                    alt={`${summaryType} Word Cloud`}
                                    className="sentiment-plot"
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "Report" && (
                        <div className={`report-tab ${fadeClass}`}>
                            <h5>Download Report</h5>
                            <p>Select the type of report you want and click download. 
                                Docx for simplified document of the computation and CSV for processed raw data</p>

                            {/* Dropdown for selecting report type */}
                            <select
                                id="report-type"
                                defaultValue=""
                                style={{ padding: "8px", borderRadius: "6px", marginBottom: "10px" }}
                            >
                                <option value="" disabled>
                                    -- Choose Report Type --
                                </option>
                                <option value="docx">📄 DOCX Report</option>
                                <option value="csv">📊 CSV Data</option>
                            </select>

                            {/* Download button */}
                            <button
                                className="download-btn"
                                onClick={() => {
                                    const type = document.getElementById("report-type").value;
                                    if (!type || !sentimentData) return;

                                    if (type === "docx") {
                                        fetch(`${config.SENTIMENTBACKENDURL}/download_report`, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                                format: "docx",
                                                Overall_sentiment: sentimentData.Overall_sentiment,
                                                Score: sentimentData.Score,
                                                Positive_summary: sentimentData.Positive_summary,
                                                Negative_summary: sentimentData.Negative_summary,
                                                selected_graph_filename: selectedGraph ? selectedGraph.filename : null,
                                                wordcloud_pos_filename: sentimentData.wordcloud_pos_filename,
                                                wordcloud_neg_filename: sentimentData.wordcloud_neg_filename,
                                                compound_csv_filename: sentimentData.compound_csv_filename,
                                            }),
                                        })
                                            .then((res) => res.blob())
                                            .then((blob) => {
                                                const link = document.createElement("a");
                                                link.href = URL.createObjectURL(blob);
                                                link.download = "Sentiment_Report.docx";
                                                link.click();
                                            })
                                            .catch((err) => console.error("DOCX Download Error:", err));
                                    } else if (type === "csv") {
                                        fetch(`${config.SENTIMENTBACKENDURL}/download_csv`, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                                filename: sentimentData.compound_csv_filename,
                                            }),
                                        })
                                            .then((res) => {
                                                if (!res.ok) throw new Error("Failed to download CSV");
                                                return res.blob();
                                            })
                                            .then((blob) => {
                                                const link = document.createElement("a");
                                                link.href = URL.createObjectURL(blob);
                                                link.download = sentimentData.compound_csv_filename;
                                                link.click();
                                            })
                                            .catch((err) => console.error("CSV Download Error:", err));
                                    }
                                }}
                            >
                                ⬇️ Download
                            </button>
                        </div>
                    )}

                    {activeTab === "Reset" && (
                        <div className={`reset-tab ${fadeClass}`}>
                            <h5>Reset Session</h5>
                            <p>This will clear the uploaded file and analysis result, returning you to the upload screen.</p>
                            <button className="download-btn" onClick={handleReset}>Reset</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SentimentAnalysis;
